import { lumi } from '@/lib/lumi'

export interface StyleTokens {
  // Colors
  colorBg: string
  colorPaper: string
  colorBeige: string
  colorInk: string
  colorInkSoft: string
  colorPrimary: string
  colorPrimaryHover: string
  colorAccent: string
  colorLine: string

  // Typography families
  fontSerif: string
  fontSans: string

  // H1 (hero)
  h1Size: string
  h1SizeMobile: string
  h1Weight: string
  h1Tracking: string

  // H2 (section)
  h2Size: string
  h2SizeMobile: string
  h2Weight: string

  // H3 (subsection)
  h3Size: string
  h3SizeMobile: string
  h3Weight: string

  // Body
  bodySize: string
  bodyWeight: string
  bodyLineHeight: string

  // Intro / italic subtitle
  introSize: string
  introSizeMobile: string

  // Eyebrow
  eyebrowSize: string
  eyebrowTracking: string
  eyebrowWeight: string

  // Buttons
  buttonFontSize: string
  buttonPaddingX: string
  buttonPaddingY: string
  buttonRadius: string
  buttonTracking: string

  // Layout
  containerMaxWidth: string
  sectionPaddingY: string
  sectionPaddingYMobile: string
}

export const DEFAULT_TOKENS: StyleTokens = {
  colorBg: '#F6EFF4',
  colorPaper: '#EFE5EC',
  colorBeige: '#E5D5DC',
  colorInk: '#1F1B1D',
  colorInkSoft: '#3A3236',
  colorPrimary: '#9E3A5A',
  colorPrimaryHover: '#7A2D46',
  colorAccent: '#C0923F',
  colorLine: '#DCC9D2',

  fontSerif: 'Cormorant Garamond, Georgia, serif',
  fontSans: 'Inter, system-ui, sans-serif',

  h1Size: '60',
  h1SizeMobile: '36',
  h1Weight: '400',
  h1Tracking: '-0.01',

  h2Size: '44',
  h2SizeMobile: '28',
  h2Weight: '400',

  h3Size: '32',
  h3SizeMobile: '26',
  h3Weight: '400',

  bodySize: '16',
  bodyWeight: '400',
  bodyLineHeight: '1.6',

  introSize: '20',
  introSizeMobile: '18',

  eyebrowSize: '11',
  eyebrowTracking: '0.3',
  eyebrowWeight: '500',

  buttonFontSize: '13',
  buttonPaddingX: '32',
  buttonPaddingY: '14',
  buttonRadius: '0',
  buttonTracking: '0.1',

  containerMaxWidth: '1280',
  sectionPaddingY: '96',
  sectionPaddingYMobile: '64',
}

/**
 * Map tokens to CSS variables and inject them into the document root.
 * This is what allows live preview and global propagation.
 */
export function applyTokensToDocument(tokens: StyleTokens) {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  // Colors
  root.style.setProperty('--bone', tokens.colorBg)
  root.style.setProperty('--paper', tokens.colorPaper)
  root.style.setProperty('--beige', tokens.colorBeige)
  root.style.setProperty('--ink', tokens.colorInk)
  root.style.setProperty('--ink-soft', tokens.colorInkSoft)
  root.style.setProperty('--terracotta', tokens.colorPrimary)
  root.style.setProperty('--bordeaux', tokens.colorAccent)
  root.style.setProperty('--line', tokens.colorLine)

  // Custom tokens (used by .hc-* classes in index.css)
  root.style.setProperty('--hc-color-primary', tokens.colorPrimary)
  root.style.setProperty('--hc-color-primary-hover', tokens.colorPrimaryHover)
  root.style.setProperty('--hc-color-accent', tokens.colorAccent)
  root.style.setProperty('--hc-color-ink', tokens.colorInk)
  root.style.setProperty('--hc-color-ink-soft', tokens.colorInkSoft)
  root.style.setProperty('--hc-color-bg', tokens.colorBg)
  root.style.setProperty('--hc-color-line', tokens.colorLine)

  // Typography
  root.style.setProperty('--hc-font-serif', tokens.fontSerif)
  root.style.setProperty('--hc-font-sans', tokens.fontSans)

  root.style.setProperty('--hc-h1-size', `${tokens.h1Size}px`)
  root.style.setProperty('--hc-h1-size-mobile', `${tokens.h1SizeMobile}px`)
  root.style.setProperty('--hc-h1-weight', tokens.h1Weight)
  root.style.setProperty('--hc-h1-tracking', `${tokens.h1Tracking}em`)

  root.style.setProperty('--hc-h2-size', `${tokens.h2Size}px`)
  root.style.setProperty('--hc-h2-size-mobile', `${tokens.h2SizeMobile}px`)
  root.style.setProperty('--hc-h2-weight', tokens.h2Weight)

  root.style.setProperty('--hc-h3-size', `${tokens.h3Size}px`)
  root.style.setProperty('--hc-h3-size-mobile', `${tokens.h3SizeMobile}px`)
  root.style.setProperty('--hc-h3-weight', tokens.h3Weight)

  root.style.setProperty('--hc-body-size', `${tokens.bodySize}px`)
  root.style.setProperty('--hc-body-weight', tokens.bodyWeight)
  root.style.setProperty('--hc-body-line-height', tokens.bodyLineHeight)

  root.style.setProperty('--hc-intro-size', `${tokens.introSize}px`)
  root.style.setProperty('--hc-intro-size-mobile', `${tokens.introSizeMobile}px`)

  root.style.setProperty('--hc-eyebrow-size', `${tokens.eyebrowSize}px`)
  root.style.setProperty('--hc-eyebrow-tracking', `${tokens.eyebrowTracking}em`)
  root.style.setProperty('--hc-eyebrow-weight', tokens.eyebrowWeight)

  root.style.setProperty('--hc-button-font-size', `${tokens.buttonFontSize}px`)
  root.style.setProperty('--hc-button-padding-x', `${tokens.buttonPaddingX}px`)
  root.style.setProperty('--hc-button-padding-y', `${tokens.buttonPaddingY}px`)
  root.style.setProperty('--hc-button-radius', `${tokens.buttonRadius}px`)
  root.style.setProperty('--hc-button-tracking', `${tokens.buttonTracking}em`)

  root.style.setProperty('--hc-container-max', `${tokens.containerMaxWidth}px`)
  root.style.setProperty('--hc-section-py', `${tokens.sectionPaddingY}px`)
  root.style.setProperty('--hc-section-py-mobile', `${tokens.sectionPaddingYMobile}px`)
}

/** Load tokens from MongoDB. Returns merged tokens (defaults + saved). */
export async function loadTokens(): Promise<{ tokens: StyleTokens; recordId: string | null }> {
  try {
    const { list } = await lumi.entities.site_styles.list({
      filter: { key: 'global' },
      limit: 1,
    })
    if (list && list.length > 0) {
      const record = list[0] as any
      return {
        tokens: { ...DEFAULT_TOKENS, ...(record.tokens || {}) },
        recordId: record._id,
      }
    }
    return { tokens: DEFAULT_TOKENS, recordId: null }
  } catch (err) {
    console.error('Failed to load site styles:', err)
    return { tokens: DEFAULT_TOKENS, recordId: null }
  }
}

/** Save tokens to MongoDB. Updates existing record or creates a new one. */
export async function saveTokens(tokens: StyleTokens, recordId: string | null): Promise<string> {
  const now = new Date().toISOString()
  if (recordId) {
    await lumi.entities.site_styles.update(recordId, {
      tokens: tokens as any,
      updatedAt: now,
    })
    return recordId
  }
  const created = await lumi.entities.site_styles.create({
    key: 'global',
    tokens: tokens as any,
    creator: 'admin',
    createdAt: now,
    updatedAt: now,
  })
  return (created as any)._id
}
